import React, { useEffect, useRef, useState } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import QRCode from "react-qr-code";
import Styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    getReceiveAsset,
    getReceiveCurrency,
    getReceiveNetwork,
    setCabinetWalletReceiveModalStatus,
} from "../../../store/cabinet/action";

import receiveIcon from "../../../theme/assets/svg/b_cabinet_receive_icon.svg";
import warningIcon from "../../../theme/assets/svg/b_cabinet_receive_warning_icon.svg";

import CryptoIcon from "../../_UI/cryptoIcon";

const CabinetWalletReceiveModal = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const dropdownRef = useRef(null);
    const copiedTimeoutRef = useRef(null);

    const modalStatus = useSelector(store => store.cabinet.cabinetWalletReceiveModalStatus);
    const receiveCurrency = useSelector(store => store.cabinet.receiveCurrency);
    const receiveNetwork = useSelector(store => store.cabinet.receiveNetwork);
    const receiveAsset = useSelector(store => store.cabinet.receiveAsset);

    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const [selectedNetwork, setSelectedNetwork] = useState(null);

    const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
    const [isNetworkDropdownOpen, setIsNetworkDropdownOpen] = useState(false);
    const [isCopied, setIsCopied] = useState(false);

    const hasCurrencyDropdown = receiveCurrency?.length > 1;
    const hasNetworkDropdown = receiveNetwork?.length > 1;

    const walletAddress = receiveAsset?.address || "";

    const selectedCurrencyCode = selectedCurrency?.code || "";
    const selectedAssetIconType = receiveAsset?.asset?.type || selectedNetwork?.type || "";
    const selectedAssetNetworkCode =
        receiveAsset?.asset?.network?.code ||
        selectedNetwork?.code ||
        selectedNetwork?.native_symbol ||
        "";

    const closeModal = () => {
        dispatch(setCabinetWalletReceiveModalStatus(false));
        setIsCurrencyDropdownOpen(false);
        setIsNetworkDropdownOpen(false);
        setIsCopied(false);

        if (copiedTimeoutRef.current) {
            clearTimeout(copiedTimeoutRef.current);
        }
    };

    const currencySelectHandler = (currency) => {
        setSelectedCurrency(currency);
        setSelectedNetwork(null);
        setIsCurrencyDropdownOpen(false);
        setIsNetworkDropdownOpen(false);
        setIsCopied(false);
    };

    const networkSelectHandler = (network) => {
        setSelectedNetwork(network);
        setIsNetworkDropdownOpen(false);
        setIsCopied(false);
    };

    const currencyDropdownToggleHandler = () => {
        if (!hasCurrencyDropdown) return;

        setIsCurrencyDropdownOpen(prev => !prev);
        setIsNetworkDropdownOpen(false);
    };

    const networkDropdownToggleHandler = () => {
        if (!hasNetworkDropdown) return;

        setIsNetworkDropdownOpen(prev => !prev);
        setIsCurrencyDropdownOpen(false);
    };

    const setCopiedStatus = () => {
        setIsCopied(true);

        if (copiedTimeoutRef.current) {
            clearTimeout(copiedTimeoutRef.current);
        }

        copiedTimeoutRef.current = setTimeout(() => {
            setIsCopied(false);
        }, 2000);
    };

    const copyAddressHandler = () => {
        if (!walletAddress) return;

        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(walletAddress)
                .then(() => {
                    setCopiedStatus();
                })
                .catch((error) => {
                    console.error("Copy failed:", error);
                });

            return;
        }

        const textarea = document.createElement("textarea");

        textarea.value = walletAddress;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";

        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();

        try {
            document.execCommand("copy");
            setCopiedStatus();
        } catch (error) {
            console.error("Copy failed:", error);
        }

        document.body.removeChild(textarea);
    };

    const shareAddressHandler = async () => {
        if (!walletAddress) return;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: t("Адрес вашего кошелька"),
                    text: walletAddress,
                });
            } catch (error) {
                if (error?.name !== "AbortError") {
                    console.error("Share failed:", error);
                }
            }

            return;
        }

        copyAddressHandler();
    };

    useEffect(() => {
        if (modalStatus) {
            dispatch(getReceiveCurrency());
        }
    }, [modalStatus, dispatch]);

    useEffect(() => {
        if (!selectedCurrency && receiveCurrency?.length) {
            setSelectedCurrency(receiveCurrency[0]);
        }
    }, [receiveCurrency, selectedCurrency]);

    useEffect(() => {
        if (selectedCurrency?.id) {
            dispatch(getReceiveNetwork(selectedCurrency.id));
        }
    }, [dispatch, selectedCurrency]);

    useEffect(() => {
        if (receiveNetwork?.length) {
            setSelectedNetwork(receiveNetwork[0]);
            return;
        }

        setSelectedNetwork(null);
    }, [receiveNetwork]);

    useEffect(() => {
        if (!selectedCurrency?.id || !selectedNetwork?.id) return;

        dispatch(getReceiveAsset(selectedCurrency.id, selectedNetwork.id));
    }, [dispatch, selectedCurrency, selectedNetwork]);

    useEffect(() => {
        setIsCopied(false);
    }, [walletAddress]);

    useEffect(() => {
        const clickOutsideHandler = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsCurrencyDropdownOpen(false);
                setIsNetworkDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", clickOutsideHandler);

        return () => {
            document.removeEventListener("mousedown", clickOutsideHandler);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (copiedTimeoutRef.current) {
                clearTimeout(copiedTimeoutRef.current);
            }
        };
    }, []);

    return (
        <Popup
            open={modalStatus}
            modal
            nested
            closeOnDocumentClick
            onClose={closeModal}
        >
            <div className={Styles.modalContainer}>
                <div
                    className={Styles.modalCloseBtn}
                    onClick={closeModal}
                >
                    <div />
                    <div />
                </div>

                <div className={Styles.modalContentWrap}>
                    <div className={Styles.titleRow}>
                        <div className={Styles.titleRowImgWrap}>
                            <img src={receiveIcon} alt={t("Пополнить кошелёк")} />
                        </div>

                        <div className={Styles.titleRowText}>
                            <div className={Styles.title}>
                                {t("Пополнить кошелёк")}
                            </div>

                            <div className={Styles.note}>
                                {t("Пополните кошелёк криптовалютой")}
                            </div>
                        </div>
                    </div>

                    <div
                        className={Styles.formWrap}
                        ref={dropdownRef}
                    >
                        <div className={Styles.formItem}>
                            <div className={Styles.formLabel}>
                                {t("Актив")}
                            </div>

                            <div className={Styles.dropdownWrap}>
                                <button
                                    type="button"
                                    className={`
                                        ${Styles.dropdownBtn}
                                        ${isCurrencyDropdownOpen ? Styles.dropdownBtnActive : ""}
                                        ${!hasCurrencyDropdown ? Styles.dropdownBtnSingle : ""}
                                    `}
                                    onClick={currencyDropdownToggleHandler}
                                    disabled={!receiveCurrency?.length}
                                >
                                    <div className={Styles.dropdownAsset}>
                                        <CryptoIcon
                                            code={selectedCurrencyCode}
                                            networkCode={selectedAssetNetworkCode}
                                            assetType={selectedAssetIconType}
                                            className={Styles.currencyIcon}
                                        />

                                        <div className={Styles.dropdownAssetText}>
                                            <div className={Styles.dropdownAssetName}>
                                                {selectedCurrency?.code || t("Выберите актив")}
                                            </div>

                                            <div className={Styles.dropdownAssetCode}>
                                                {selectedCurrency?.name || ""}
                                            </div>
                                        </div>
                                    </div>

                                    <div className={Styles.dropdownBalance}>
                                        {t("Баланс")}: 0 {selectedCurrency?.code || ""}
                                    </div>

                                    {hasCurrencyDropdown && (
                                        <div className={Styles.dropdownArrow} />
                                    )}
                                </button>

                                {isCurrencyDropdownOpen && hasCurrencyDropdown && (
                                    <div className={Styles.dropdownList}>
                                        <div className={Styles.dropdownListScroll}>
                                            {receiveCurrency?.map((currency) => {
                                                const isActive = selectedCurrency?.id === currency.id;

                                                return (
                                                    <button
                                                        type="button"
                                                        className={`
                                                            ${Styles.dropdownListItem}
                                                            ${isActive ? Styles.dropdownListItemActive : ""}
                                                        `}
                                                        key={currency.id}
                                                        onClick={() => currencySelectHandler(currency)}
                                                    >
                                                        <CryptoIcon
                                                            code={currency.code}
                                                            className={Styles.currencyIcon}
                                                        />

                                                        <div className={Styles.dropdownAssetText}>
                                                            <div className={Styles.dropdownAssetName}>
                                                                {currency.code}
                                                            </div>

                                                            <div className={Styles.dropdownAssetCode}>
                                                                {currency.name}
                                                            </div>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={Styles.formItem}>
                            <div className={Styles.formLabel}>
                                {t("Сеть")}
                            </div>

                            <div className={Styles.dropdownWrap}>
                                <button
                                    type="button"
                                    className={`
                                        ${Styles.dropdownBtn}
                                        ${isNetworkDropdownOpen ? Styles.dropdownBtnActive : ""}
                                        ${!hasNetworkDropdown ? Styles.dropdownBtnSingle : ""}
                                    `}
                                    onClick={networkDropdownToggleHandler}
                                    disabled={!receiveNetwork?.length}
                                >
                                    <div className={Styles.dropdownAsset}>
                                        <CryptoIcon
                                            code={selectedCurrencyCode}
                                            networkCode={selectedNetwork?.code || selectedNetwork?.native_symbol}
                                            assetType={selectedNetwork?.type || selectedAssetIconType}
                                            className={Styles.currencyIcon}
                                        />

                                        <div className={Styles.dropdownAssetText}>
                                            <div className={Styles.dropdownAssetName}>
                                                {selectedNetwork?.name || t("Выберите сеть")}
                                            </div>

                                            <div className={Styles.dropdownAssetCode}>
                                                {selectedNetwork?.code?.toUpperCase() || ""}
                                            </div>
                                        </div>
                                    </div>

                                    <div className={Styles.dropdownBalance}>
                                        {selectedNetwork?.confirmations ? (
                                            <>
                                                {selectedNetwork.confirmations} {t("подтверждений")}
                                            </>
                                        ) : null}
                                    </div>

                                    {hasNetworkDropdown && (
                                        <div className={Styles.dropdownArrow} />
                                    )}
                                </button>

                                {isNetworkDropdownOpen && hasNetworkDropdown && (
                                    <div className={Styles.dropdownList}>
                                        <div className={Styles.dropdownListScroll}>
                                            {receiveNetwork?.map((network) => {
                                                const isActive = selectedNetwork?.id === network.id;

                                                return (
                                                    <button
                                                        type="button"
                                                        className={`
                                                            ${Styles.dropdownListItem}
                                                            ${isActive ? Styles.dropdownListItemActive : ""}
                                                        `}
                                                        key={network.id}
                                                        onClick={() => networkSelectHandler(network)}
                                                    >
                                                        <CryptoIcon
                                                            code={selectedCurrencyCode}
                                                            networkCode={network?.code || network?.native_symbol}
                                                            assetType={network?.type}
                                                            className={Styles.currencyIcon}
                                                        />

                                                        <div className={Styles.dropdownAssetText}>
                                                            <div className={Styles.dropdownAssetName}>
                                                                {network.name}
                                                            </div>

                                                            <div className={Styles.dropdownAssetCode}>
                                                                {network.code?.toUpperCase()}
                                                            </div>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={Styles.addressWrap}>
                        <div className={Styles.qrWrap}>
                            {walletAddress ? (
                                <QRCode
                                    value={walletAddress}
                                    size={256}
                                    bgColor="#FFFFFF"
                                    fgColor="#000000"
                                    level="M"
                                    className={Styles.qrCode}
                                />
                            ) : (
                                <div className={Styles.qrPlaceholder}>
                                    QR
                                </div>
                            )}
                        </div>

                        <div className={Styles.addressContent}>
                            <div className={Styles.addressTitle}>
                                {t("Адрес вашего кошелька")}
                            </div>

                            <div className={Styles.addressBox}>
                                {walletAddress || t("Адрес пока не найден")}
                            </div>

                            <div className={Styles.addressBtns}>
                                <button
                                    type="button"
                                    className={`${Styles.copyBtn} ${isCopied ? Styles.copyBtnCopied : ""}`}
                                    onClick={copyAddressHandler}
                                    disabled={!walletAddress}
                                >
                                    <svg
                                        width="22"
                                        height="22"
                                        viewBox="0 0 22 22"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <rect
                                            x="7"
                                            y="7"
                                            width="11"
                                            height="13"
                                            rx="2"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        />

                                        <path
                                            d="M4 15V4C4 2.89543 4.89543 2 6 2H15"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>

                                    {isCopied ? t("Скопировано") : t("Скопировать адрес")}
                                </button>

                                <button
                                    type="button"
                                    className={Styles.shareBtn}
                                    onClick={shareAddressHandler}
                                    disabled={!walletAddress}
                                >
                                    <svg
                                        width="22"
                                        height="22"
                                        viewBox="0 0 22 22"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M11 14V3"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />

                                        <path
                                            d="M7 7L11 3L15 7"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />

                                        <path
                                            d="M5 10V17C5 18.1046 5.89543 19 7 19H15C16.1046 19 17 18.1046 17 17V10"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>

                                    {t("Поделиться")}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={Styles.warningRow}>
                        <div className={Styles.warningRowImgWrap}>
                            <img src={warningIcon} alt="" />
                        </div>

                        <div className={Styles.warningRowText}>
                            <div className={Styles.mainText}>
                                {t("Отправляйте только")}{" "}
                                {selectedCurrency?.name || selectedCurrency?.code || ""}{" "}
                                {t("на этот адрес сети")}{" "}
                                {selectedNetwork?.name ? `(${selectedNetwork.name})` : ""}
                            </div>

                            <div className={Styles.subText}>
                                {t("Отправка другого актива или выбор другой сети может привести к безвозвратной потере средств.")}
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className={Styles.closeBtn}
                        onClick={closeModal}
                    >
                        {t("Закрыть")}
                    </button>
                </div>
            </div>
        </Popup>
    );
};

export default CabinetWalletReceiveModal;
